/**
 * ActivateAccountPage — Landing des Einladungs-Links („Konto aktivieren").
 *
 * Liest userId + token aus der Query (`/activate-account?userId=…&token=…&workspace=…`), lässt
 * den Eingeladenen sein erstes Passwort setzen und ruft `POST /auth/activate-account`. Kein
 * Auto-Login (Backend gibt nur ein Ergebnis) → nach Erfolg zurück zur Anmeldung, mit dem
 * Unternehmenskürzel aus dem Link vorbefüllt. Ansprache „du“ wie alle Anmeldeflächen (Board A).
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '@/shared/api';
import { AuthPageHeader } from './AuthPageHeader';
import { AuthStatusPage } from './AuthStatusPage';
import { authBackLinkClass } from './authLinkStyles';
import { loginWorkspacePrefill, type LoginSearch } from './loginPrefill';
import { NewPasswordForm } from './NewPasswordForm';
import { passwordFailure, type PasswordOutcome } from './passwordFailure';

export function ActivateAccountPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    userId?: string;
    token?: string;
    workspace?: unknown;
  };
  const userId = search.userId ?? '';
  const token = search.token ?? '';
  // Das Kürzel aus dem Einladungslink geht an die Anmeldung weiter, der Token nie.
  const workspace = loginWorkspacePrefill(search.workspace);
  const loginSearch: LoginSearch = workspace ? { workspace } : {};
  const [outcome, setOutcome] = useState<PasswordOutcome | null>(null);

  if (outcome?.kind === 'activated')
    return (
      <AuthStatusPage
        eyebrow="Einladung"
        title="Dein Konto ist schon aktiv"
        text="Melde dich mit deinem Passwort an. Weißt du es nicht mehr, setz es über „Passwort vergessen?“ zurück."
        main={{ label: 'Zur Anmeldung', to: '/login' }}
        loginSearch={loginSearch}
      />
    );
  if (!userId || !token || outcome?.kind === 'expired')
    return (
      <AuthStatusPage
        eyebrow="Einladung"
        title={outcome ? 'Dieser Link gilt nicht mehr' : 'Dieser Link ist unvollständig'}
        tone={outcome ? 'error' : 'note'}
        text={
          outcome
            ? 'Der Server hat den Link abgelehnt: er ist abgelaufen oder wurde schon benutzt. Bitte deinen Administrator um eine neue Einladung.'
            : 'Dem Link fehlt ein Teil. Bitte deinen Administrator um eine neue Einladung.'
        }
        main={{ label: 'Zur Anmeldung', to: '/login' }}
        loginSearch={loginSearch}
      />
    );

  return (
    <>
      <AuthPageHeader
        eyebrow={workspace ? `Einladung · ${workspace}` : 'Einladung'}
        title="Konto aktivieren"
        description="Vergib ein Passwort. Danach meldest du dich mit deinem Kürzel an."
      />
      <NewPasswordForm
        submitLabel="Konto aktivieren"
        pendingLabel="Wird aktiviert …"
        failure={outcome?.kind === 'card' ? outcome : null}
        onSubmit={async (password) => {
          setOutcome(null);
          try {
            await api.auth.activateAccount({ userId, token, password });
            toast.success('Konto aktiviert – melde dich jetzt mit deinem Passwort an.');
            void navigate({ to: '/login', search: loginSearch });
          } catch (error) {
            setOutcome(await passwordFailure(error));
          }
        }}
      />
      <Link to="/login" search={loginSearch} className={`${authBackLinkClass} self-center`}>
        Zurück zur Anmeldung
      </Link>
    </>
  );
}
